// Composables
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized
} from 'vue-router'
import { useAuth } from '@/composables/auth'
import { useAppShell } from '@/composables/appShell'
import { useSnackbarStore } from '../store/snackbar'
import { clearAuthSession } from '@/utils/session'
import { capturePostHogPageview, trackPostHogEvent } from '@/utils/posthog'

const routes = [
  {
    path: '/login',
    children: [
      {
        path: '',
        name: 'Login',
        component: () => import('@/views/Login.vue')
      }
    ],
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/forgot-password',
    children: [
      {
        path: '',
        name: 'ForgotPassword',
        component: () => import('@/views/ForgotPassword.vue')
      }
    ],
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/',
    children: [
      {
        path: '/new-password/:username',
        name: 'NewPassword',
        component: () => import('@/views/NewPassword.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/',
    children: [
      {
        path: '/reset/:username',
        name: 'Reset',
        component: () => import('@/views/Reset.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/',
        name: 'Home',
        component: () => import('@/views/Home.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/onr',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '',
        name: 'ONRPlayground',
        component: () => import('@/views/onr/OnrPlaygroundView.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/user-profile',
        name: 'UserProfile',
        component: () => import('@/views/UserProfile.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/settings',
        name: 'Settings',
        component: () => import('@/views/Settings.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/transaction-score-settings',
        name: 'TransactionScoreSettings',
        component: () => import('@/views/TransactionScoreSettings.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/robots',
        name: 'Robots',
        component: () => import('@/views/Robots.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/users',
        name: 'Users',
        component: () => import('@/views/Users.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/api-keys',
        name: 'ApiKeys',
        component: () => import('@/views/ApiKeys.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/api-monitoring',
        name: 'ApiMonitoring',
        component: () => import('@/views/ApiMonitoring.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/users-management',
        name: 'UsersManagement',
        component: () => import('@/views/Users.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/companies',
        name: 'Companies',
        component: () => import('@/views/Companies.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/document-types',
        name: 'DocumentTypes',
        component: () => import('@/views/DocumentTypes.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence',
        name: 'Diligence',
        component: () => import('@/views/Diligence.vue')
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence-kanban',
        name: 'DiligenceKanban',
        component: () => import('@/views/DiligenceKanban.vue')
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:id',
        name: 'Entity',
        component: () => import('@/views/Entity.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:id/sight',
        name: 'DiligenceSight',
        component: () => import('@/views/DiligenceSight.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:id/standard-report',
        name: 'DiligenceStandardReport',
        component: () => import('@/views/DiligenceStandardReport.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:id/custom-report',
        name: 'DiligenceCustomReport',
        component: () => import('@/views/DiligenceCustomReport.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/entity/:entityId',
        name: 'Documents',
        component: () => import('@/views/Documents.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/entity/:entityId/dossier/:processId',
        name: 'Dossier',
        component: () => import('@/views/Dossier.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/entity/:entityId/personal-document/:documentId',
        name: 'PersonalDocumentAnalysis',
        component: () => import('@/views/PersonalDocumentAnalysis.vue'),
        props: true
      },
      {
        path: '/diligence/:diligenceId/:documentId',
        name: 'DocumentAnalysis',
        component: () => import('@/views/DocumentAnalysis.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/registration/:registrationId',
        name: 'RegistrationAnalysis',
        component: () => import('@/views/RegistrationAnalysis.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/entity/:entityId/docs',
        name: 'EntityDocs',
        component: () => import('@/views/EntityDocs.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/entity/:entityId/qsa',
        name: 'EntitiesQSA',
        component: () => import('@/views/QSAEntitiesView.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/entity/:entityId/spc-search',
        name: 'SpcSearch',
        component: () => import('@/views/SpcCreditAnalysisView.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/docs/:id',
        name: 'DiligenceDocs',
        component: () => import('@/views/DiligenceDocuments.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/tracks',
        name: 'Tracks',
        component: () => import('@/views/Tracks.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/tracks/:id',
        name: 'EditTrack',
        component: () => import('@/views/EditTrack.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/review-track',
        name: 'ReviewTrack',
        component: () => import('@/views/ReviewTrack.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/new-track',
        name: 'NewTrack',
        component: () => import('@/views/NewTrack.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/tracks-simplified',
        name: 'TracksSimplified',
        component: () => import('@/views/TracksSimplified.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/tracks-simplified/:id',
        name: 'EditTrackSimplified',
        component: () => import('@/views/SimplifiedTrackFlow.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/new-track-simplified',
        name: 'NewTrackSimplified',
        component: () => import('@/views/SimplifiedTrackFlow.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/commercial',
        name: 'Commercial',
        component: () => import('@/views/Commercial.vue'),
        props: true
      },
      {
        path: '/commercial/billing-dashboard',
        name: 'BillingDashboard',
        component: () => import('@/views/BillingUsageDashboard.vue'),
        props: true
      },
      {
        path: '/commercial/legacy-companies-metrics',
        name: 'LegacyCompaniesMetrics',
        component: () => import('@/views/LegacyCompaniesMetrics.vue'),
        props: true
      },
      {
        path: '/commercial/robot-debug',
        name: 'CommercialRobotDebug',
        component: () => import('@/views/CommercialRobotDebug.vue'),
        props: true
      },
      {
        path: '/coverage',
        name: 'Coverage',
        component: () => import('@/views/Coverage.vue'),
        props: true
      },
      {
        path: '/track-calculator',
        name: 'TrackCalculator',
        component: () => import('@/views/TrackCalculator.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:diligenceId/law-suits/:entityId',
        name: 'LawSuits',
        component: () => import('@/views/LawSuits.vue'),
        props: true
      },
      {
        path: '/diligence/:diligenceId/processes',
        name: 'DiligenceLawSuits',
        component: () => import('@/views/DiligenceLawSuits.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/financial-statement',
        name: 'FinancialStatement',
        component: () => import('@/views/FinancialStatement.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/financial-statement/:companyId',
        name: 'FinancialStatementCompany',
        component: () => import('@/views/FinancialStatementCompany.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/terms',
        name: 'Terms',
        component: () => import('@/views/Terms.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/smart-fill-templates',
        name: 'SmartFillTemplates',
        component: () => import('@/views/SmartFillTemplates.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/smart-fill-template/:id',
        name: 'SmartFillTemplateSetup',
        component: () => import('@/views/SmartFillTemplateSetup.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/smart-fill-processing/:diligenceId/:runId',
        name: 'SmartFillRunProcessing',
        component: () => import('@/views/SmartFillRunProcessing.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/smart-fill-review/:runId',
        name: 'SmartFillReview',
        component: () => import('@/views/SmartFillTemplateSetup.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/template',
        name: 'Template',
        component: () => import('@/views/Template.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/template/:id',
        name: 'TemplateAnalysis',
        component: () => import('@/views/TemplateAnalysis.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:id/contract/:contractId',
        name: 'GenerateContract',
        component: () => import('@/views/GenerateContract.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/transfer/:id',
        name: 'Transfer',
        component: () => import('@/views/Transfer.vue'),
        props: true
      },
      {
        path: '/transfer/:id/units',
        name: 'TransferUnits',
        component: () => import('@/views/TransferUnits.vue'),
        props: true
      },
      {
        path: '/transfer/:id/units/:unitId',
        name: 'TransferUnitComparison',
        component: () => import('@/views/TransferUnits.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/batch/:id',
        name: 'BatchEntities',
        component: () => import('@/views/BatchEntities.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/batch/:id/review',
        name: 'ReviewBatchEntities',
        component: () => import('@/views/ReviewBatchEntities.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/document-processor/:id',
        name: 'DocumentProcessor',
        component: () => import('@/views/DocumentProcessor.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/document-details/:diligenceId/:fileId',
        name: 'DocumentDetails',
        component: () => import('@/views/DocumentDetails.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/workspace',
        name: 'Workspace',
        component: () => import('@/views/Workspace.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },

  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/diligence/:id/external-upload',
        name: 'ExternalUploadSettings',
        component: () => import('@/views/ExternalUploadSettings.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/external-upload/:shortCode',
    children: [
      {
        path: '',
        name: 'ExternalUploadPortal',
        component: () => import('@/views/ExternalUploadPortal.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/reports',
        name: 'Reports',
        component: () => import('@/views/Reports.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: '/document-list/:id',
        name: 'DocumentList',
        component: () => import('@/views/DocumentList.vue'),
        props: true
      }
    ],
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/status/documents',
    children: [
      {
        path: '',
        name: 'DocumentsHealth',
        component: () => import('@/views/DocumentsHealth.vue')
      }
    ],
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/login'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// @ts-ignore
const redirectToLogin = (next) => {
  const appShell = useAppShell()
  const snackbarStore = useSnackbarStore()
  snackbarStore.showSnackbar({
    text: 'Tempo de sessão expirado',
    color: 'warning',
    timeout: 4000
  })

  appShell.setLoading(false)
  clearAuthSession()
  return next('/login')
}

router.beforeEach(async (to, from, next) => {
  const appShell = useAppShell()
  const isInitialNavigation = !from.name

  // Durante o callback OAuth do Cognito (SSO), ?code= e ?state= estão na URL.
  // Processamos a sessão aqui mesmo, sem renderizar nenhuma rota protegida.
  if (to.query.code && to.query.state) {
    appShell.setLoading(true, 'Finalizando login SSO...')
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth')
      const session = await fetchAuthSession()
      const userId = session.tokens?.idToken?.payload?.sub as string | undefined

      if (userId) {
        const auth = useAuth()
        await auth.fetchAndStoreUser(userId)
      }
    } catch (e) {
      console.error('Erro ao processar callback SSO:', e)
      clearAuthSession()
      appShell.setLoading(false)
      return next({ name: 'Login' })
    }
    // Redireciona para Home com URL limpa (sem ?code=&state=)
    return next({ name: 'Home' })
  }

  const auth = useAuth()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  if (requiresAuth && isInitialNavigation) {
    appShell.setLoading(true, 'Verificando sessão...')
  }
  const isAuthenticated = !(await auth.isJwtExpired())

  if (to.path === '/login' && isAuthenticated) {
    appShell.setLoading(false)
    return next('/')
  }

  if (requiresAuth) {
    if (!isAuthenticated) return redirectToLogin(next)

    // @ts-ignore
    const allowed = auth.hasPermission(to.name)
    // Se hasPermission retornar undefined/null (role ainda não carregado), deixa passar
    if (allowed === false) {
      appShell.setLoading(false)
      return next('/')
    }
    return next()
  }

  appShell.setLoading(false)
  return next()
})

const emitRouteAnalytics = (to: RouteLocationNormalized) => {
  const routeName = String(to.name || '')
  const diligenceId = String(to.params.id || to.params.diligenceId || '')
  const entityId = String(to.params.entityId || '')
  const runId = String(to.params.runId || '')
  const baseProperties = {
    route_name: routeName,
    route_path: to.path,
    diligence_id: diligenceId,
    entity_id: entityId,
    run_id: runId
  }

  trackPostHogEvent('page_view', baseProperties)

  if (routeName === 'Entity') {
    trackPostHogEvent('diligence_opened', baseProperties)
  }

  if (['Documents', 'EntityDocs', 'EntitiesQSA'].includes(routeName)) {
    trackPostHogEvent('entity_opened', baseProperties)
  }

  if (['LawSuits', 'DiligenceLawSuits'].includes(routeName)) {
    trackPostHogEvent('process_view', baseProperties)
  }

  if (routeName === 'DiligenceCustomReport') {
    trackPostHogEvent('custom_report_opened', baseProperties)
  }

  if (routeName === 'SpcSearch') {
    trackPostHogEvent('spc_search_opened', baseProperties)
  }

  if (to.path.includes('/faq')) {
    trackPostHogEvent('faq_used', baseProperties)
  }
}

router.afterEach((to) => {
  const appShell = useAppShell()
  appShell.setLoading(false)

  capturePostHogPageview(to.fullPath, {
    route_name: String(to.name || ''),
    route_path: to.path
  })
  emitRouteAnalytics(to)
})

export default router
